import timeParser from '../../../helpers/timeParser';
import constants from '../../../helpers/constants';
import reportHelpers from '../include/helpers';
import * as echarts from '../../../libs/echarts';

function getTextValue(timeValue) {
  if (timeValue) {
    const text = timeParser.output(timeValue, {
      prop: 'estimation',
    });

    if (timeValue < 0) {
      return `- ${text}`;
    }

    return text;
  }

  if (timeValue === 0) {
    return `0 ${__('hour_in_time_string')}`;
  }

  return '';
}

const locales = {
  logs: 'report_time_by_projects_logs',
  estimation: 'report_time_by_projects_estimation',
  baselineEstimation: 'report_time_by_projects_baseline',
  variance: 'report_page_variance',
};

const getLabelsByCalculationMode = calculationMode => {
  let labels = [locales.logs, locales.estimation];

  if (calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
    labels = [locales.logs, locales.baselineEstimation];
  }

  if (calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
    labels = [locales.estimation, locales.baselineEstimation];
  }

  labels.push(locales.variance);

  return labels;
};

export default {
  monthReport: {
    default: {
      tooltip: {
        show: true,
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: 'transparent',
          },
          label: {
            show: false,
          },
        },
        formatter: data => {
          const bars = data.filter(item => item.componentSubType === 'bar');
          const lines = data.filter(item => item.componentSubType === 'line');

          const barVarianceValue = (bars[0]?.value - bars[1]?.value).toFixed(2);
          const lineVarianceValue = (lines[0]?.value - lines[1]?.value).toFixed(2);
          const axisValueLabel = bars[0]?.axisValueLabel || lines[0]?.axisValueLabel;
          const values = axisValueLabel.split(' ');

          return `<div class="chart_tooltip">
                  <div class="tooltip_title">${__(`${values[0]}_full`)} ${values[1]}</div>
                  ${bars[0] ? `<div class="tooltip_info">
                    <div class="marker">${bars[0]?.marker}</div>
                    <div class="name">${__(`${bars[0]?.data.customData.label}_tooltip`)}</div>
                    <div class="value">${getTextValue(bars[0]?.data.customData.value)}</div>
                  </div>` : ''}
                  ${bars[1] ? `<div class="tooltip_info">
                    <div class="marker">${bars[1]?.marker}</div>
                    <div class="name">${__(`${bars[1]?.data.customData.label}_tooltip`)}</div>
                    <div class="value">${getTextValue(bars[1]?.data.customData.value)}</div>
                  </div>` : ''}
                  ${bars.length === 2 ? `<div class="tooltip_info margin-bottom-8">
                    <div class="name">${__('report_page_variance')}</div>
                    <div class="value">${getTextValue(barVarianceValue)}</div>
                  </div>` : ''}
                  ${lines[0] ? `<div class="tooltip_info">
                    <div class="marker">${lines[0]?.marker}</div>
                    <div class="name">${__(`${lines[0]?.data.customData.label}_tooltip`)}</div>
                    <div class="value">${getTextValue(lines[0]?.data.customData.value)}</div>
                  </div>` : ''}
                  ${lines[1] ? `<div class="tooltip_info">
                    <div class="marker">${lines[1]?.marker}</div>
                    <div class="name">${__(`${lines[1]?.data.customData.label}_tooltip`)}</div>
                    <div class="value">${getTextValue(lines[1]?.data.customData.value)}</div>
                  </div>` : ''}
                  ${lines.length === 2 ? `<div class="tooltip_info">
                    <div class="name">${__('report_page_variance_cumulative')}</div>
                    <div class="value">${getTextValue(lineVarianceValue)}</div>
                  </div>` : ''}
                </div>`;
        },
        borderColor: '#808080',
        borderWidth: 1,
        borderRadius: 4,
        padding: 8,
        backgroundColor: '#FFFFFF',
        extraCssText: 'box-shadow: 0px 9px 22px -2px rgba(0, 0, 0, 0.2);',
      },
      legend: {
        data: [],
      },
      xAxis: [
        {
          type: 'category',
          data: [],
          axisPointer: {
            type: 'shadow',
          },
          axisLabel: {
            show: true,
            color: '#191919',
            padding: [19, 0, 0, 0],
            fontSize: 16,
            lineHeight: 19,
            fontFamily: 'Lato-Regular, sans-serif',
            formatter: value => {
              const values = value.split(' ');

              return __(`${values[0]}`);
            },
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: '',
          min: 0,
          max: 'dataMax',
          axisLabel: {
            show: true,
            formatter: value => `${getTextValue(value)}`,
            color: '#999999',
            fontSize: 15,
            lineHeight: 18,
            fontFamily: 'Lato-Regular, sans-serif',
          },
        },
        {
          type: 'value',
          name: __('report_graphic_cumulative_label'),
          nameTextStyle: {
            color: '#999999',
            fontSize: 12,
            lineHeight: 14,
            fontFamily: 'Lato-Bold, sans-serif',
          },
          min: 0,
          max: 'dataMax',
          axisLabel: {
            show: true,
            formatter: value => `${getTextValue(value)}`,
            color: '#999999',
            fontSize: 15,
            lineHeight: 18,
            fontFamily: 'Lato-Regular, sans-serif',
          },
        },
      ],
      series: [
        {
          name: 'estimation',
          locale: 'report_time_by_month_estimation',
          colorBy: 'series',
          color: '#8BC34A',
          disabledColor: '#CCCCCC',
          type: 'bar',
          label: {
            show: true,
            color: '#FFFFFF',
            fontSize: 12,
            lineHeight: 14,
            fontFamily: 'Lato-Bold, sans-serif',
          },
          labelLayout: params => ({
            fontSize: (params.rect.height < 14 || params.rect.width < 30) ? 0 : 12,
          }),
          data: [],
        },
        {
          name: 'logs',
          locale: 'report_time_by_month_logs',
          type: 'bar',
          colorBy: 'series',
          // color: '#4484CD',
          color: '#73A3D9',
          disabledColor: '#E9E9E9',
          label: {
            show: true,
            color: '#FFFFFF',
            fontSize: 12,
            lineHeight: 14,
            fontFamily: 'Lato-Bold, sans-serif',
          },
          labelLayout: params => ({
            fontSize: (params.rect.height < 14 || params.rect.width < 30) ? 0 : 12,
          }),
          data: [],
        },
        {
          name: 'baselineEstimation',
          locale: 'report_time_by_month_basleline_estimation',
          type: 'bar',
          colorBy: 'series',
          // color: '#4484CD',
          color: '#73A3D9',
          disabledColor: '#E9E9E9',
          label: {
            show: true,
            color: '#FFFFFF',
            fontSize: 12,
            lineHeight: 14,
            fontFamily: 'Lato-Bold, sans-serif',
          },
          labelLayout: params => ({
            fontSize: (params.rect.height < 14 || params.rect.width < 30) ? 0 : 12,
          }),
          data: [],
        },
        {
          name: 'cumulativeEstimation',
          locale: 'report_time_by_month_cumulative_estimation',
          type: 'line',
          colorBy: 'series',
          color: '#6E8F3F',
          disabledColor: '#999999',
          yAxisIndex: 1,
          data: [],
        },
        {
          name: 'cumulativeLogs',
          locale: 'report_time_by_month_cumulative_logs',
          type: 'line',
          colorBy: 'series',
          color: '#1565C0',
          disabledColor: '#CCCCCC',
          yAxisIndex: 1,
          data: [],
        },
        {
          name: 'cumulativeBaselineEstimation',
          locale: 'report_time_by_month_basleline_cumulative_estimation',
          type: 'line',
          colorBy: 'series',
          color: '#1565C0',
          disabledColor: '#CCCCCC',
          yAxisIndex: 1,
          data: [],
        },
      ],
      silent: false,
      animation: true,
    },
    stub: {
      tooltip: {
        show: false,
      },
      legend: {
        data: [],
      },
      xAxis: [
        {
          type: 'category',
          data: [1, 2, 3, 4, 5, 6],
          axisLabel: {
            show: false,
          },
          axisPointer: {
            type: 'shadow',
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: '',
          min: 0,
          max: 1000,
          interval: 200,
          axisLabel: {
            show: false,
            formatter: '$ {value}',
          },
        },
        {
          type: 'value',
          name: '',
          min: 0,
          interval: 650,
          max: 3250,
          axisLabel: {
            show: false,
            formatter: '$ {value}',
          },
        },
      ],
      series: [
        {
          name: 'totalCost',
          colorBy: 'series',
          color: '#CCCCCC',
          type: 'bar',
          label: {
            show: false,
          },
          data: [400, 800, 400, 600, 200, 600],
        },
        {
          name: 'actualCost',
          type: 'bar',
          colorBy: 'series',
          color: '#E9E9E9',
          label: {
            show: false,
          },
          data: [600, 400, 200, 400, 200, 1000],
        },
        {
          name: 'cumTotalCost',
          type: 'line',
          colorBy: 'series',
          color: '#999999',
          yAxisIndex: 1,
          label: {
            show: false,
          },
          data: [500, 1300, 1700, 2400, 2600, 3250],
        },
        {
          name: 'cumTotalCost',
          type: 'line',
          colorBy: 'series',
          color: '#CCCCCC',
          yAxisIndex: 1,
          label: {
            show: false,
          },
          data: [650, 1000, 1300, 1460, 1950, 3000],
        },
      ],
      silent: true,
      animation: false,
    },
  },
  projectsReport: calculationMode => ({
    default: {
      legend: {
        show: false,
      },
      tooltip: {
        show: true,
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        borderColor: '#808080',
        borderWidth: 1,
        borderRadius: 4,
        padding: 8,
        backgroundColor: '#FFFFFF',
        extraCssText: 'box-shadow: 0px 9px 22px -2px rgba(0, 0, 0, 0.2);',
        formatter: params => `<div class="chart_tooltip">
          <div class="tooltip_title">${params[0].axisValueLabel}</div>
            ${params[0] ? `<div class="tooltip_info">
              <div class="marker">${params[0]?.marker}</div>
              <div class="name">${__(`${locales[params[0].data.label.name]}_tooltip`)}</div>
              <div class="value">${getTextValue(params[0].value)}</div>
            </div>` : ''}
            ${params[1] ? `<div class="tooltip_info">
              <div class="marker">${params[1]?.marker}</div>
              <div class="name">${__(`${locales[params[1].data.label.name]}_tooltip`)}</div>
              <div class="value">${getTextValue(params[1].value)}</div>
            </div>` : ''}
            ${params[2] ? `<div class="tooltip_info">
              <div class="marker">${params[2]?.marker}</div>
              <div class="name">${__(`${locales[params[2].data.label.name]}_tooltip`)}</div>
              <div class="value">${getTextValue(params[2].value)}</div>
            </div>` : ''}
          </div>
        </div>`,
      },
      xAxis: {
        type: 'category',
        data: [],
        axisLabel: {
          show: true,
          interval: 0,
          overflow: 'truncate',
          color: '#191919',
          padding: [19, 0, 0, 0],
          fontSize: 16,
          lineHeight: 19,
          fontFamily: 'Lato-Regular, sans-serif',
          formatter: value => {
            const chartInstance = echarts.getInstanceByDom(document.querySelector('[_echarts_instance_]'));
            const chartDom = document.querySelector('#echart_id');
            const options = chartInstance.getOption();

            const chartWidth = chartDom.clientWidth * 0.8;
            const seriesWidth = (Math.floor(chartWidth / (options.xAxis[0].data.length || 1)));
            const truncated = reportHelpers.truncateTextToTwoLinesWithNewline(value, (seriesWidth / 2), '16px Lato');

            return `${truncated}`;
          },
        },
      },
      yAxis: {
        axisLabel: {
          show: true,
          formatter: value => `${getTextValue(value)}`,
          color: '#999999',
          fontSize: 15,
          lineHeight: 18,
          fontFamily: 'Lato-Regular, sans-serif',
        },
      },
      series: [
        {
          type: 'bar',
          name: 'time_graphic_data_1',
          data: [],
          colorBy: 'series',
          color: '#8BC34A',
          disabledColor: '#CCCCCC',
          barMinHeight: 0.5,
          label: {
            show: true,
            text: () => getLabelsByCalculationMode(calculationMode)[0],
            fontSize: 12,
            lineHeight: 14,
            fontFamily: 'Lato-Bold, sans-serif',
          },
          labelLayout: params => ({
            fontSize: ((params.rect.height < 14 && params.rect.height !== 0.5) || params.rect.width < 40) ? 0 : 12,
          }),
        },
        {
          type: 'bar',
          name: 'time_graphic_data_2',
          data: [],
          colorBy: 'series',
          color: '#73A3D9',
          disabledColor: '#E9E9E9',
          barMinHeight: 0.5,
          label: {
            show: true,
            position: 'inside',
            text: () => getLabelsByCalculationMode(calculationMode)[1],
            fontSize: 12,
            lineHeight: 14,
            fontFamily: 'Lato-Bold, sans-serif',
          },
          labelLayout: params => ({
            fontSize: ((params.rect.height < 14 && params.rect.height !== 0.5) || params.rect.width < 40) ? 0 : 12,
          }),
        },
        {
          type: 'bar',
          name: 'variance',
          data: [],
          colorBy: 'series',
          color: '#A051B7',
          disabledColor: '#999999',
          barMinHeight: 0.5,
          label: {
            show: true,
            position: 'inside',
            text: () => getLabelsByCalculationMode(calculationMode)[2],
            fontSize: 12,
            lineHeight: 14,
            fontFamily: 'Lato-Bold, sans-serif',
          },
          labelLayout: params => ({
            fontSize: ((params.rect.height < 14 && params.rect.height !== 0.5) || params.rect.width < 40) ? 0 : 12,
          }),
        },
      ],
      silent: false,
      animation: true,
    },
    stub: {
      legend: {
        show: false,
      },
      tooltip: {
        show: false,
      },
      xAxis: {
        type: 'category',
        data: ['1', '2', '3'],
        axisLabel: { show: false },
      },
      yAxis: {
        axisLabel: {
          show: false,
          formatter: '{value}h',
        },
      },
      series: [
        {
          type: 'bar',
          name: 'time_graphic_data_1',
          data: [6, 12, 6],
          colorBy: 'series',
          color: '#CCCCCC',
          label: { show: false },
        },
        {
          type: 'bar',
          name: 'time_graphic_data_2',
          data: [9, 5.8, 3],
          colorBy: 'series',
          color: '#E9E9E9',
          label: { show: false },
        },
        {
          type: 'bar',
          name: 'variance',
          data: [3, 6.9, -3],
          colorBy: 'series',
          color: '#999999',
          label: { show: false },
        },
      ],
      silent: true,
      animation: false,
    },
  }),
  helpers: {
    getTextValue,
  },
};
